"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Stack,
  Fade,
  Zoom,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Code,
  CheckCircle,
  ContentCopy,
  Download,
  Visibility,
  EditNote,
  Close,
  InsertDriveFile,
} from "@mui/icons-material";

interface MarkdownFile {
  file: File;
  id: string;
  content: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function MarkdownToHtmlConverter() {
  const [markdownFile, setMarkdownFile] = useState<MarkdownFile | null>(null);
  const [markdownText, setMarkdownText] = useState<string>("");
  const [htmlOutput, setHtmlOutput] = useState<string>("");
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [inputTab, setInputTab] = useState(0);
  const [outputTab, setOutputTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isValidExtension =
      fileName.endsWith(".md") ||
      fileName.endsWith(".markdown") ||
      fileName.endsWith(".txt");

    if (
      isValidExtension ||
      file.type === "text/markdown" ||
      file.type === "text/plain"
    ) {
      const content = await file.text();
      setMarkdownFile({
        file,
        id: Math.random().toString(36).substr(2, 9),
        content,
      });
      setMarkdownText(content);
      setError(null);
      setSuccess(false);
      setHtmlOutput("");
    } else {
      setError(
        `${file.name} is not a supported format. Please use .md, .markdown, or .txt files.`
      );
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setMarkdownFile(null);
    setMarkdownText("");
    setError(null);
    setSuccess(false);
    setHtmlOutput("");
    const fileInput = document.getElementById(
      "markdown-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const convertToHtml = async () => {
    const textToConvert = markdownText.trim();

    if (!textToConvert) {
      setError("Please enter some Markdown text or upload a file to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await axios.post(
        "/api/markdown-to-html",
        { markdown: textToConvert },
        {
          timeout: 600000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setHtmlOutput(response.data.html);
      setSuccess(true);
    } catch (err) {
      let errorMessage = "Failed to convert Markdown to HTML";
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data;
        errorMessage = errorData.error || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(htmlOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadHtml = () => {
    const blob = new Blob([htmlOutput], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName =
      markdownFile?.file.name.replace(/\.(md|markdown|txt)$/i, "") ||
      "converted";
    a.download = `${fileName}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleInputTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setInputTab(newValue);
  };

  const handleOutputTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setOutputTab(newValue);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Input Method Tabs */}
        <Tabs
          value={inputTab}
          onChange={handleInputTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<EditNote />} label="Type Markdown" iconPosition="start" />
          <Tab
            icon={<CloudUpload />}
            label="Upload File"
            iconPosition="start"
          />
        </Tabs>

        {/* Type Markdown Tab */}
        <TabPanel value={inputTab} index={0}>
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder="# Enter your Markdown here...

## Example

This is a **bold** text and this is *italic*.

- List item 1
- List item 2

\`\`\`javascript
const greeting = 'Hello, World!';
console.log(greeting);
\`\`\`"
            value={markdownText}
            onChange={(e) => {
              setMarkdownText(e.target.value);
              setSuccess(false);
              setHtmlOutput("");
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
        </TabPanel>

        {/* Upload File Tab */}
        <TabPanel value={inputTab} index={1}>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: "3px dashed",
              borderColor: dragActive ? "primary.main" : "grey.300",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              bgcolor: dragActive ? "primary.50" : "grey.50",
              cursor: "pointer",
              transition: "all 0.3s ease",
              position: "relative",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "primary.50",
              },
            }}
            onClick={() =>
              document.getElementById("markdown-file-input")?.click()
            }
          >
            <input
              id="markdown-file-input"
              type="file"
              accept=".md,.markdown,.txt,text/markdown,text/plain"
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: "none" }}
            />

            <CloudUpload
              sx={{
                fontSize: 80,
                color: dragActive ? "primary.main" : "grey.400",
                mb: 2,
              }}
            />

            <Typography
              variant="h6"
              component="p"
              gutterBottom
              fontWeight={600}
              color="text.primary"
            >
              {dragActive
                ? "Drop your Markdown file here"
                : "Click to upload or drag and drop"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Supported formats: .md, .markdown, .txt
            </Typography>

            <Button
              variant="contained"
              startIcon={<InsertDriveFile sx={{ color: "#ffffff" }} />}
              sx={{
                mt: 3,
                color: "#ffffff",
                backgroundColor: "#5558d3",
                "&:hover": {
                  backgroundColor: "#4448c2",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById("markdown-file-input")?.click();
              }}
            >
              Select Markdown File
            </Button>
          </Box>

          {/* Selected File Preview */}
          {markdownFile && (
            <Fade in={!!markdownFile}>
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" component="p" fontWeight={600}>
                    Selected File
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={removeFile}
                    color="error"
                    variant="outlined"
                  >
                    Remove
                  </Button>
                </Box>

                <Zoom in>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        bgcolor: "#1976D2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Code sx={{ fontSize: 32, color: "white" }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {markdownFile.file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(markdownFile.file.size)}
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={removeFile}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          bgcolor: "error.main",
                          color: "white",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Paper>
                </Zoom>
              </Box>
            </Fade>
          )}
        </TabPanel>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            Markdown converted to HTML successfully!
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            onClick={convertToHtml}
            disabled={converting || !markdownText.trim()}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to HTML"}
          </Button>
        </Box>

        {/* HTML Output */}
        {htmlOutput && (
          <Fade in={!!htmlOutput}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  HTML Output
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={copyToClipboard}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={downloadHtml}
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    }}
                  >
                    Download
                  </Button>
                </Stack>
              </Box>

              <Tabs
                value={outputTab}
                onChange={handleOutputTabChange}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab icon={<Code />} label="HTML Code" iconPosition="start" />
                <Tab
                  icon={<Visibility />}
                  label="Preview"
                  iconPosition="start"
                />
              </Tabs>

              <TabPanel value={outputTab} index={0}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mt: 2,
                    bgcolor: "#1e1e1e",
                    borderRadius: 2,
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      color: "#d4d4d4",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                    }}
                  >
                    {htmlOutput}
                  </Typography>
                </Paper>
              </TabPanel>

              <TabPanel value={outputTab} index={1}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mt: 2,
                    borderRadius: 2,
                    maxHeight: "400px",
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    dangerouslySetInnerHTML={{ __html: htmlOutput }}
                    sx={{
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        mt: 2,
                        mb: 1,
                        fontWeight: 600,
                      },
                      "& h1": { fontSize: "2rem" },
                      "& h2": { fontSize: "1.5rem" },
                      "& h3": { fontSize: "1.25rem" },
                      "& p": { mb: 2 },
                      "& ul, & ol": { pl: 3, mb: 2 },
                      "& code": {
                        bgcolor: "grey.100",
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.9em",
                      },
                      "& pre": {
                        bgcolor: "grey.100",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        "& code": {
                          bgcolor: "transparent",
                          p: 0,
                        },
                      },
                      "& blockquote": {
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        pl: 2,
                        ml: 0,
                        fontStyle: "italic",
                        color: "text.secondary",
                      },
                      "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        mb: 2,
                      },
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "divider",
                        p: 1,
                      },
                      "& th": {
                        bgcolor: "grey.100",
                        fontWeight: 600,
                      },
                      "& a": {
                        color: "primary.main",
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                      },
                    }}
                  />
                </Paper>
              </TabPanel>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
